import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import clsx from 'clsx';

import { TableCellCheckbox } from '~components/generic/table/TableCellCheckbox';
import { TableCellName } from '~components/generic/table/TableCellName';
import useLabels from '~hooks/useLabels';
import styles from '~components/generic/table/Table.module.css';

export type TableDataItemType = 'user' | 'group';

export type TableDataItemShape = {
  name: string;
  id: number;
  view: boolean;
  update: boolean;
  create: boolean;
  delete: boolean;
  publish: boolean;
  set_grants: boolean;
  view_draft: boolean;
  type: TableDataItemType;
};
export type TableDataShape = TableDataItemShape[];

export type TableProps = {
  data: TableDataShape;
  updateData: (rowIndex: number, columnId: string, value: boolean) => void;
  disabled: boolean;
  canChange: boolean;
};

export const Table = ({ data, updateData, disabled, canChange }: TableProps) => {
  const {
    tableHeaderPermissionsRole,
    tableHeaderPermissionsView,
    tableHeaderPermissionsUpdate,
    tableHeaderPermissionsCreate,
    tableHeaderPermissionsDelete,
    tableHeaderPermissionsViewDraft,
    tableHeaderPermissionsPublish,
    tableHeaderPermissionsSetGrants,
  } = useLabels([
    'tableHeaderPermissionsRole',
    'tableHeaderPermissionsView',
    'tableHeaderPermissionsUpdate',
    'tableHeaderPermissionsCreate',
    'tableHeaderPermissionsDelete',
    'tableHeaderPermissionsViewDraft',
    'tableHeaderPermissionsPublish',
    'tableHeaderPermissionsSetGrants',
  ]);

  const columns = [
    {
      header: () => <span>{tableHeaderPermissionsRole}</span>,
      accessorKey: 'name',
      cell: ({ getValue, row }) => <TableCellName value={getValue()} row={row} />,
    },
    {
      header: () => <span>{tableHeaderPermissionsView}</span>,
      accessorKey: 'view',
      cell: ({ row, column }) => (
        <TableCellCheckbox
          value={row.original.view}
          row={row}
          updateData={updateData}
          column={column}
          canChange={canChange}
          disabled={disabled}
          showMinusOnEmptyCheckbox={true}
        />
      ),
    },
    {
      header: () => <span>{tableHeaderPermissionsUpdate}</span>,
      accessorKey: 'update',
      cell: ({ row, column }) => (
        <TableCellCheckbox
          value={row.original.update}
          row={row}
          updateData={updateData}
          column={column}
          canChange={canChange}
          disabled={disabled}
          showMinusOnEmptyCheckbox={true}
        />
      ),
    },
    {
      header: () => <span>{tableHeaderPermissionsCreate}</span>,
      accessorKey: 'create',
      cell: ({ row, column }) => (
        <TableCellCheckbox
          value={row.original.create}
          row={row}
          updateData={updateData}
          column={column}
          canChange={canChange}
          disabled={disabled}
          showMinusOnEmptyCheckbox={true}
        />
      ),
    },
    {
      header: () => <span>{tableHeaderPermissionsDelete}</span>,
      accessorKey: 'delete',
      cell: ({ row, column }) => (
        <TableCellCheckbox
          value={row.original.delete}
          row={row}
          updateData={updateData}
          column={column}
          canChange={canChange}
          disabled={disabled}
          showMinusOnEmptyCheckbox={true}
        />
      ),
    },
    {
      header: () => <span>{tableHeaderPermissionsViewDraft}</span>,
      accessorKey: 'view_draft',
      cell: ({ row, column }) => (
        <TableCellCheckbox
          value={row.original.view_draft}
          row={row}
          updateData={updateData}
          column={column}
          canChange={canChange}
          disabled={disabled}
          showMinusOnEmptyCheckbox={true}
        />
      ),
    },
    {
      header: () => <span>{tableHeaderPermissionsPublish}</span>,
      accessorKey: 'publish',
      cell: ({ row, column }) => (
        <TableCellCheckbox
          value={row.original.publish}
          row={row}
          updateData={updateData}
          column={column}
          canChange={canChange}
          disabled={disabled}
          showMinusOnEmptyCheckbox={true}
        />
      ),
    },
    {
      header: () => <span>{tableHeaderPermissionsSetGrants}</span>,
      accessorKey: 'set_grants',
      cell: ({ row, column }) => (
        <TableCellCheckbox
          value={row.original.set_grants}
          row={row}
          updateData={updateData}
          column={column}
          canChange={canChange}
          disabled={disabled}
          showMinusOnEmptyCheckbox={true}
        />
      ),
    },
  ];
  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className={styles.container}>
      <table className={styles.table}>
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => {
                return (
                  <th key={header.id} colSpan={header.colSpan} className={clsx(styles.th, index === 0 && styles.th30)}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>

        <tbody>
          {table.getRowModel().rows.map(row => (
            <tr
              key={row.id}
              className={clsx(
                styles.tr,
                row.original.type === 'user' && styles.trUser,
                row.original.type === 'group' && styles.trGroup,
              )}
            >
              {row.getVisibleCells().map(cell => (
                <td key={cell.id} className={styles.td}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
